import { ReactNode, useEffect } from 'react';
import { LoginPage } from '../features/login/login.page';
import { useAppContext } from '../hooks/use-app-context.hook';
import { StorageKey } from '../constants';
import { useStorage } from '../hooks/use-storage';
import { useLoadData } from '../hooks/useLoadData';
import { User } from '../models/user';
import { usersApiKeyStore } from '../api/users-api-key-store';
import { LoadingIndicator } from '../components/loading-indicator.component';
import { useQueryClient } from 'react-query';
import { ApiResponse } from '../models/api-response';
import { ForgotPasswordEmail } from '../features/password/forget-password-email.page';
import { ResetForgetPassword } from '../features/password/reset-forget-password';

import { useLocation } from 'react-router-dom';
import { useSaveDataForPostInDux } from '../hooks/useSaveData';
import { loginApiKeyStore } from '../api/login-api-key-store';
import { LoginResponse, LoginResponseForDux } from '../models/login-info';
import { ScheduleReminderPage } from '../features/schedule-reminder/schedule-reminder.page';

interface AuthorizeComponentProps {
  children: ReactNode;
}

const isDUXEnabled = process.env.REACT_APP_DUX_FLAG === 'true';

export const AuthorizeComponent = ({ children }: AuthorizeComponentProps) => {
  const queryClient = useQueryClient();
  const { appData, setAppData } = useAppContext();
  const [authToken, setAuthToken] = useStorage<string | undefined>(StorageKey.AUTH_TOKEN, undefined);
  const { isLoading, isFetching, data, isError } = useLoadData<ApiResponse<User>>(
    usersApiKeyStore.getUserDetails(),
    !!authToken
  );

  const {
    isLoading: isLoadingDux,
    isError: isErrorDux,
    mutateAsync: mutateAsyncDux,
    error: duxError,
  } = useSaveDataForPostInDux(loginApiKeyStore.duxLogin(), undefined);

  const pathname = window.location.pathname;

  // const location = useLocation(); 

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const option = queryParams.get("Option");

  useEffect(() => {
    const updateUserDetails = async (user: any) => {
      try {
        // Call mutateAsyncDux and pass the user data as required

        let resultDux; // const resultDux = await mutateAsyncDux({ data: { userName: 'jgeorge@wbpro.com', userKey: 'J@654321' } });
        if (isDUXEnabled) {
          resultDux = await mutateAsyncDux({ data: { userName: user?.email, userKey: `${user.userGUID}` } });
        }
        if (resultDux && resultDux?.token && resultDux?.loggedInUser) {
          localStorage.setItem('duxusertoken', resultDux?.token);
          localStorage.setItem('duxwidgetuserid', resultDux?.loggedInUser?.userGUID);
        }
      } catch (error) {
        // console.error('mutateAsyncDux failed:', error); // Log the error, but don't block setAppData
      } finally {
        // Always run setAppData after the attempt to call mutateAsyncDux
        setAppData({
          user: { ...user, userPermissions: user.permissions?.map((p: any) => p.permissionName) },
        });
      }
    };

    if (!isLoading && !isFetching && data) {
      const user: any = data.data;
      updateUserDetails(user); // Call the function to handle mutateAsyncDux and setAppData
    }
  }, [data, isLoading, isFetching, mutateAsyncDux, setAppData]);

  // useEffect(() => {
  //   if (!isLoading && !isFetching && data) {
  //     const user: any = data.data;
  //     setAppData({
  //       user: { ...user, userPermissions: user.permissions?.map((p: any) => p.permissionName) },
  //     });
  //   }
  // }, [data, isLoading, isFetching]);
  useEffect(() => {
    const channel = new BroadcastChannel('auth');

    const handleMessage = (message: MessageEvent) => {
      if (message?.data?.type === 'logout') {
        // Handle logout in other tabs

        window.location.href = '/login'; // Navigate or reload
      }
    };

    channel.addEventListener('message', handleMessage);

    return () => {
      channel.close(); // Close the channel on cleanup
    };
  }, []);

  useEffect(() => {
    if (isError) {
      setAuthToken(undefined);
    }
  }, [isError]);

  const loginSuccessHandler = (token: string, refreshToken: string) => {
    setAuthToken(token);
    queryClient.invalidateQueries(usersApiKeyStore.getUserDetails().queryKey);
  };

  // console.log(pathname, token);

  if (pathname.includes('/ScheduleReminder')) {
    return <ScheduleReminderPage token={token ?? ''} option={option ?? ''} />;
  }

  if (pathname.includes('/forgot-password/reset') && authToken === undefined) {
    return <ResetForgetPassword />;
  }

  if (pathname == '/forgot-password' && authToken === undefined) {
    return <ForgotPasswordEmail />;
  }

  if (authToken === undefined || isError) {
    return <LoginPage onLoginSuccess={loginSuccessHandler} />;
  }

  if (appData.user.userID) {
    return <div>{children}</div>;
  }
  return <LoadingIndicator isLoading isSpinner />;
};
